import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import { useNavigate } from "react-router-dom";
import { GET_URL } from "Includes/urls";
import { getRequest } from "Includes/api/apicall";
import { se } from "date-fns/locale";

function ERPFeaturesDigitalMarketingFeaturesList() {
  const history = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // ✅ SAME AS COMPANY LIST
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [rowCount, setRowCount] = useState(0);
  const [category, setCategory] = useState("erp");

  // Menu state for edit option
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRowId, setSelectedRowId] = useState(null);

  // 🔥 FETCH FUNCTION (SAME STYLE)
  const getData = (nextPage = page, nextPageSize = pageSize) => {
    const url =
      category === "erp"
        ? GET_URL.erpfeature.api
        : GET_URL.digitalmarketingfeature.api;

    setLoading(true);

    getRequest(url, {
      limit: nextPageSize,
      pageno: nextPage + 1,
    })
      .then((res) => {
        const data = res?.data?.data || [];

        setRows(
          data.map((item) => ({
            ...item,
            category_type: category === "erp" ? "ERP" : "Digital",
          }))
        );

        setRowCount(res?.data?.count || data.length);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // 🔥 EFFECT (LIKE COMPANY LIST)
  useEffect(() => {
    getData(page, pageSize);
  }, [page, pageSize, category]);

  // 🔥 HANDLE PAGE CHANGE
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // 🔥 HANDLE PAGE SIZE CHANGE
  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(0); // ✅ important
  };

  // 🔥 RESET WHEN CATEGORY CHANGES
  useEffect(() => {
    setPage(0);
  }, [category]);

  // 🔥 NAVIGATION
  const handleAddFeature = () => {
    history(
      "/billing/erp-features-digital-marketing-features/add", { state: { type: category } }
    );
  };

  const handleMenuClick = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRowId(row); // passing full row instead of just id
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRowId(null);
  };

  const handleEdit = () => {
    if (selectedRowId) {
      history("/billing/erp-features-digital-marketing-features/edit", {
        state: {
          detail: selectedRowId.id,
          type: selectedRowId.category_type === "ERP" ? "erp" : "digital",
          rowData: selectedRowId
        }
      });
    }
    handleMenuClose();
  };

// ✅ COLUMNS
const columns = [
  { field: "id", headerName: "ID", width: 90, options: { display: false } },
  { field: "name", headerName: "Feature Name", flex: 1 },
  { field: "category_type", headerName: "Type", width: 140 },
  {
    field: "erp_category",
    headerName: "ERP Category",
    width: 180,
    valueGetter: (params) =>
      params.row.category_type === "ERP"
        ? params.row.category || "N/A"
        : "N/A",
  },
  {
    field: "calculation_type",
    headerName: "Calculation",
    width: 150,
    valueGetter: (params) => {
      if (params.row.category_type !== "Digital") return "N/A";
      const labels = {
        yearwise: "Year Wise",
        monthwise: "Month Wise",
        studentcount: "Student Count",
      };
      return labels[params.row.calculation_type] || params.row.calculation_type || "N/A";
    },
  },
  {
    field: "price",
    headerName: "Price",
    width: 140,
    valueGetter: (params) =>
      params.row.price ? `₹ ${params.row.price}` : "N/A",
  },
  {
    field: "renewal_price",
    headerName: "Renewal Price",
    width: 140,
    valueGetter: (params) =>
      params.row.renewal_price ? `₹ ${params.row.renewal_price}` : "N/A",
  },
  {
    field: "created",
    headerName: "Created At",
    width: 200,
    valueGetter: (params) =>
      params.row.created
        ? new Date(params.row.created).toLocaleString()
        : "",
  },
  {
    field: "actions",
    headerName: "Actions",
    width: 100,
    sortable: false,
    renderCell: (params) => {
      return (
        <IconButton onClick={(e) => handleMenuClick(e, params.row)}>
          <MoreVertIcon />
        </IconButton>
      );
    },
  },
];

return (
  <Box sx={{ p: 2 }}>
    {/* HEADER */}
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        mb: 2,
      }}
    >
      <Typography variant="h5" fontWeight={600}>
        ERP & Other Services
      </Typography>

      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={handleAddFeature}
      >
        Add Feature
      </Button>
    </Box>

    {/* FILTER */}
    <Paper sx={{ p: 1.5, mb: 2 }}>
      <ToggleButtonGroup
        value={category}
        exclusive
        onChange={(e, val) => val && setCategory(val)}
        size="small"
      >
        <ToggleButton value="erp">ERP</ToggleButton>
        <ToggleButton value="digital">
          Other Service
        </ToggleButton>
      </ToggleButtonGroup>
    </Paper>

    {/* TABLE */}
    <Paper sx={{ height: 500 }}>
      <DataGrid
        rows={rows}
        columns={columns}
        loading={loading}
        pagination
        paginationMode="server"

        // ✅ SAME AS YOUR WORKING CODE
        page={page}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}

        rowsPerPageOptions={[10, 20, 50]}
        rowCount={rowCount}

        disableSelectionOnClick
      />
    </Paper>

    {/* 3 Dots Menu */}
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={handleMenuClose}
    >
      <MenuItem onClick={handleEdit}>
        <EditIcon sx={{ mr: 1 }} fontSize="small" />
        Edit
      </MenuItem>
    </Menu>
  </Box>
);
}

export default ERPFeaturesDigitalMarketingFeaturesList;
